import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { TrustScoreRing, TrustScoreBreakdown, DataMaturityBadge } from '../../components/TrustScore/TrustScore';
import { BadgeList } from '../../components/Badges/VerificationBadge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { MapPin, Phone, Globe, Star, Clock, DollarSign, Loader2, ArrowLeft, Mail, Navigation, Map, ZoomIn, Award, Flag, Upload, X, AlertTriangle, Calendar, CheckCircle } from 'lucide-react';

// Lazy load the map component
const CoverageMap = lazy(() => import('../../components/CoverageMap/CoverageMap'));

function ServerProfilePage() {
  var params = useParams();
  var id = params.id;
  var searchParamsHook = useSearchParams();
  var searchParams = searchParamsHook[0];
  var showQuoteModal = searchParams.get('quote') === 'true';
  
  // Get location params from URL for distance calculation
  var fromZip = searchParams.get('from_zip');
  var fromCounty = searchParams.get('from_county');
  var fromCity = searchParams.get('from_city');
  var fromState = searchParams.get('from_state');

  var serverState = useState(null);
  var server = serverState[0];
  var setServer = serverState[1];
  
  var reviewsState = useState([]);
  var reviews = reviewsState[0];
  var setReviews = reviewsState[1];
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var quoteOpenState = useState(showQuoteModal);
  var quoteOpen = quoteOpenState[0];
  var setQuoteOpenRaw = quoteOpenState[1];
  
  var submittingState = useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];
  
  // Track selected coverage area for dynamic quote card
  var selectedAreaState = useState(null);
  var selectedArea = selectedAreaState[0];
  var setSelectedArea = selectedAreaState[1];

  var quoteFormState = useState({
    client_name: '',
    client_company: '',
    client_email: '',
    client_phone: '',
    service_street: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    document_type: '',
    urgency: 'standard',
    num_serves: 1,
    additional_details: '',
    // New fee-related fields
    estimated_miles: '',
    pages_to_print: '',
    extra_attempts: ''
  });
  var quoteForm = quoteFormState[0];
  var setQuoteForm = quoteFormState[1];
  
  // Track ZIP lookup result for estimate calculation
  var zipLookupState = useState(null);
  var zipLookup = zipLookupState[0];
  var setZipLookup = zipLookupState[1];
  
  // Complaint dialog state
  var complaintOpenState = useState(false);
  var complaintOpen = complaintOpenState[0];
  var setComplaintOpen = complaintOpenState[1];
  
  // Mileage calculation state
  var mileageCalcState = useState({ 
    calculating: false, 
    calculated: false, 
    totalMiles: null, 
    billableMiles: null, 
    freeMiles: null,
    closestOffice: null,
    error: null,
    isEstimate: false
  });
  var mileageCalc = mileageCalcState[0];
  var setMileageCalc = mileageCalcState[1];
  
  // Track verified address coordinates (from autocomplete selection)
  var verifiedCoordsState = useState(null);
  var verifiedCoords = verifiedCoordsState[0];
  var setVerifiedCoords = verifiedCoordsState[1];
  
  // Coverage check state
  var coverageCheckState = useState({
    checked: false,
    checking: false,
    isInCoverage: null,
    blockSubmission: false,
    message: null
  });
  var coverageCheck = coverageCheckState[0];
  var setCoverageCheck = coverageCheckState[1];
  
  // Wrapper for setQuoteOpen that handles form state on open/close
  function setQuoteOpen(open) {
    if (open) {
      // Opening: Reset form to initial state, then pre-fill from selected area
      var freshForm = {
        client_name: '',
        client_company: '',
        client_email: '',
        client_phone: '',
        service_street: '',
        service_city: '',
        service_state: '',
        service_zip: '',
        document_type: '',
        urgency: 'standard',
        num_serves: 1,
        additional_details: '',
        estimated_miles: '',
        pages_to_print: '',
        extra_attempts: ''
      };
      
      // Pre-fill location from selected coverage area
      if (selectedArea) {
        if (selectedArea.city) {
          freshForm.service_city = selectedArea.city;
          freshForm.service_state = selectedArea.state;
        } else if (selectedArea.county) {
          // For county, leave city empty but set state
          freshForm.service_state = selectedArea.state;
        } else if (selectedArea.type === 'state') {
          freshForm.service_state = selectedArea.state;
        } else if (selectedArea.zip_codes && selectedArea.zip_codes.length > 0) {
          // For ZIP, set the first ZIP code and let the lookup populate city/state
          freshForm.service_zip = selectedArea.zip_codes[0];
          freshForm.service_state = selectedArea.state;
        }
      }
      
      setQuoteForm(freshForm);
      // Reset mileage calculation state
      setMileageCalc({ 
        calculating: false, 
        calculated: false, 
        totalMiles: null, 
        billableMiles: null, 
        freeMiles: null,
        closestOffice: null,
        error: null,
        isEstimate: false
      });
      setZipLookup(null);
      setVerifiedCoords(null);
      // Reset coverage check state
      setCoverageCheck({
        checked: false,
        checking: false,
        isInCoverage: null,
        blockSubmission: false,
        message: null
      });
    }
    setQuoteOpenRaw(open);
  }
  
  var mapRef = useRef(null);

  useEffect(function() {
    var fetchData = function() {
      // Build location params for distance calculation
      var locationParams = {};
      if (fromZip) locationParams.from_zip = fromZip;
      if (fromCounty) locationParams.from_county = fromCounty;
      if (fromCity) locationParams.from_city = fromCity;
      if (fromState) locationParams.from_state = fromState;
      
      Promise.all([
        api.getServer(id, locationParams),
        api.getServerReviews(id, { limit: 10 })
      ]).then(function(results) {
        setServer(results[0].data);
        setReviews(results[1].data.reviews);
        setLoading(false);
      }).catch(function(error) {
        console.error('Error fetching server:', error);
        toast.error('Failed to load server profile');
        setLoading(false);
      });
    };
    fetchData();
  }, [id, fromZip, fromCounty, fromCity, fromState]);

  // Auto-calculate mileage when service address is complete
  useEffect(function() {
    // Check if we have a complete address
    var hasStreet = quoteForm.service_street && quoteForm.service_street.trim().length > 0;
    var hasCity = quoteForm.service_city && quoteForm.service_city.trim().length > 0;
    var hasState = quoteForm.service_state && quoteForm.service_state.trim().length === 2;
    var hasZip = quoteForm.service_zip && quoteForm.service_zip.trim().length >= 5;
    
    // Check if server has mileage configured
    var defaultPricing = server && server.default_pricing ? server.default_pricing : {};
    var hasMileageRate = defaultPricing.mileage_rate != null;
    
    if (hasStreet && hasCity && hasState && hasZip && hasMileageRate && server) {
      // Debounce the API call
      var timeoutId = setTimeout(function() {
        setMileageCalc(function(prev) { return Object.assign({}, prev, { calculating: true, error: null }); });
        
        // Include verified coordinates if available
        var mileageRequest = {
          street: quoteForm.service_street,
          city: quoteForm.service_city,
          state: quoteForm.service_state,
          zip: quoteForm.service_zip
        };
        
        if (verifiedCoords) {
          mileageRequest.lat = verifiedCoords.lat;
          mileageRequest.lng = verifiedCoords.lng;
          mileageRequest.verified = true;
        }
        
        api.calculateMileage(server.id, mileageRequest).then(function(response) {
          var data = response.data;
          if (data.success) {
            setMileageCalc({
              calculating: false,
              calculated: true,
              totalMiles: data.roundtrip_miles,
              billableMiles: data.billable_miles,
              freeMiles: data.free_miles,
              closestOffice: data.closest_office,
              error: null,
              isEstimate: data.is_estimate || false
            });
            // Update the form with the calculated roundtrip miles
            setQuoteForm(function(prev) { 
              return Object.assign({}, prev, { estimated_miles: String(data.roundtrip_miles) }); 
            });
          } else {
            setMileageCalc({
              calculating: false,
              calculated: false,
              totalMiles: null,
              billableMiles: null,
              freeMiles: null,
              closestOffice: null,
              error: data.error || 'Could not calculate distance',
              isEstimate: false
            });
          }
        }).catch(function(error) {
          setMileageCalc({
            calculating: false,
            calculated: false,
            totalMiles: null,
            billableMiles: null,
            freeMiles: null,
            closestOffice: null,
            error: 'Failed to calculate distance'
          });
        });
      }, 800); // Debounce 800ms
      
      return function() { clearTimeout(timeoutId); };
    } else if (!hasStreet || !hasCity || !hasState || !hasZip) {
      // Reset mileage calculation if address is incomplete
      if (mileageCalc.calculated) {
        setMileageCalc({ 
          calculating: false, 
          calculated: false, 
          totalMiles: null, 
          billableMiles: null, 
          freeMiles: null,
          closestOffice: null,
          error: null,
          isEstimate: false
        });
        setQuoteForm(function(prev) { 
          return Object.assign({}, prev, { estimated_miles: '' }); 
        });
      }
    }
  }, [quoteForm.service_street, quoteForm.service_city, quoteForm.service_state, quoteForm.service_zip, server, verifiedCoords]);

  // Check coverage when service address changes
  useEffect(function() {
    var hasCity = quoteForm.service_city && quoteForm.service_city.trim().length > 0;
    var hasState = quoteForm.service_state && quoteForm.service_state.trim().length === 2;
    var hasZip = quoteForm.service_zip && quoteForm.service_zip.trim().length >= 5;
    
    // Need at least state and either city or ZIP to check coverage
    if (!server || !hasState || (!hasCity && !hasZip)) {
      if (coverageCheck.checked) {
        setCoverageCheck({
          checked: false,
          checking: false,
          isInCoverage: null,
          blockSubmission: false,
          message: null
        });
      }
      return;
    }
    
    // Debounce coverage check
    var timeoutId = setTimeout(function() {
      setCoverageCheck(function(prev) { 
        return Object.assign({}, prev, { checking: true }); 
      });
      
      api.checkCoverage(server.id, {
        city: quoteForm.service_city,
        state: quoteForm.service_state,
        zip: quoteForm.service_zip,
        county: zipLookup && zipLookup.found ? zipLookup.county : null
      }).then(function(response) {
        var data = response.data;
        setCoverageCheck({
          checked: true,
          checking: false,
          isInCoverage: data.is_in_coverage,
          blockSubmission: data.block_submission,
          message: data.message
        });
      }).catch(function() {
        setCoverageCheck({
          checked: true,
          checking: false,
          isInCoverage: null,
          blockSubmission: false,
          message: null
        });
      });
    }, 500);
    
    return function() { clearTimeout(timeoutId); };
  }, [quoteForm.service_city, quoteForm.service_state, quoteForm.service_zip, server, zipLookup]);

  function handleQuoteSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    
    // Calculate the estimate to include in submission
    var estimate = calculateEstimate();
    
    // Build payload with enhanced address data if available
    var payload = {
      server_id: id,
      client_name: quoteForm.client_name,
      client_company: quoteForm.client_company,
      client_email: quoteForm.client_email,
      client_phone: quoteForm.client_phone,
      service_street: quoteForm.service_street,
      service_city: quoteForm.service_city,
      service_state: quoteForm.service_state,
      service_zip: quoteForm.service_zip,
      document_type: quoteForm.document_type,
      urgency: quoteForm.urgency,
      num_serves: parseInt(quoteForm.num_serves),
      additional_details: quoteForm.additional_details
    };
    
    // Include verified address data if autocomplete was used
    if (verifiedCoords) {
      payload.verified_address = verifiedCoords.fullAddress || null;
      payload.address_lat = verifiedCoords.lat;
      payload.address_lng = verifiedCoords.lng;
      payload.address_verified = true;
    } else {
      payload.address_verified = false;
    }
    
    // Include additional fee inputs
    if (quoteForm.estimated_miles) {
      payload.estimated_miles = parseFloat(quoteForm.estimated_miles) || null;
      // Calculate billable miles
      var freeMiles = (server && server.default_pricing && server.default_pricing.free_miles) || 0;
      var totalMiles = parseFloat(quoteForm.estimated_miles) || 0;
      payload.billable_miles = Math.max(0, totalMiles - freeMiles);
    }
    if (quoteForm.pages_to_print) {
      payload.pages_per_packet = parseInt(quoteForm.pages_to_print) || null;
    }
    if (quoteForm.extra_attempts) {
      payload.extra_attempts = parseInt(quoteForm.extra_attempts) || null;
    }
    
    // Include calculated estimate breakdown
    if (estimate) {
      payload.calculated_total = estimate.total;
      payload.fee_breakdown = estimate.breakdown;
    }
    
    api.requestQuote(payload).then(function() {
      toast.success('Quote request sent successfully!');
      setQuoteOpen(false);
      setQuoteForm({
        client_name: '',
        client_company: '',
        client_email: '',
        client_phone: '',
        service_street: '',
        service_city: '',
        service_state: '',
        service_zip: '',
        document_type: 'summons',
        urgency: 'standard',
        num_serves: 1,
        additional_details: '',
        estimated_miles: '',
        pages_to_print: '',
        extra_attempts: ''
      });
      setSubmitting(false);
    }).catch(function(error) {
      toast.error('Failed to send quote request');
      setSubmitting(false);
    });
  }

  function calculateEstimate() {
    if (!server) return null;
    
    var coverageAreas = server.coverage_areas || [];
    var defaultPricing = server.default_pricing || {};
    var defaultRushFee = defaultPricing.rush_fee;
    var defaultSameDayFee = defaultPricing.same_day_fee;
    
    // Try to find matching coverage area based on service address
    var baseFee = null;
    var areaRushFee = null;  // Track the matched area's rush fee
    var areaSameDayFee = null;  // Track the matched area's same day fee
    var serviceCity = (quoteForm.service_city || '').trim();
    var serviceState = (quoteForm.service_state || '').toUpperCase();
    var serviceZip = quoteForm.service_zip || '';
    
    // Use ZIP lookup result if available
    var lookupCity = zipLookup && zipLookup.found ? zipLookup.city : null;
    var lookupCounty = zipLookup && zipLookup.found ? zipLookup.county : null;
    var lookupState = zipLookup && zipLookup.found ? zipLookup.state : null;
    
    // Helper to set fees from matched area
    function setFeesFromArea(area) {
      baseFee = area.base_fee;
      areaRushFee = area.rush_fee;
      areaSameDayFee = area.same_day_fee;
    }
    
    // Priority 0: If user has selected an area from the sidebar, use that exact pricing
    if (selectedArea && selectedArea.base_fee != null) {
      // Check if the form's location matches the selected area
      var formCity = serviceCity.toLowerCase();
      var formState = serviceState || lookupState;
      
      // If form is empty or matches selected area, use selected area's price
      if (!serviceCity && !serviceZip) {
        setFeesFromArea(selectedArea);
      } else if (selectedArea.city && formCity === selectedArea.city.toLowerCase() && formState === selectedArea.state) {
        setFeesFromArea(selectedArea);
      } else if (selectedArea.county && lookupCounty === selectedArea.county && (formState === selectedArea.state || lookupState === selectedArea.state)) {
        setFeesFromArea(selectedArea);
      } else if (selectedArea.zip_codes && selectedArea.zip_codes.includes(serviceZip)) {
        setFeesFromArea(selectedArea);
      } else if (selectedArea.type === 'state' && (formState === selectedArea.state || lookupState === selectedArea.state)) {
        setFeesFromArea(selectedArea);
      }
    }
    
    // Priority 1: Check for exact city match (from form input or ZIP lookup)
    if (baseFee === null && (serviceCity || lookupCity)) {
      var checkCity = serviceCity || lookupCity;
      var checkState = serviceState || lookupState;
      if (checkCity && checkState) {
        var cityArea = coverageAreas.find(function(a) {
          return a.type === 'city' && a.city && a.city.toLowerCase() === checkCity.toLowerCase() && a.state === checkState;
        });
        if (cityArea) {
          setFeesFromArea(cityArea);
        }
      }
    }
    
    // Priority 2: Check for exact ZIP coverage
    if (baseFee === null && serviceZip) {
      var zipArea = coverageAreas.find(function(a) {
        return a.zip_codes && a.zip_codes.includes(serviceZip);
      });
      if (zipArea) {
        setFeesFromArea(zipArea);
      }
    }
    
    // Priority 3: Check for county match from ZIP lookup
    if (baseFee === null && serviceZip && lookupCounty && lookupState) {
      var countyArea = coverageAreas.find(function(a) {
        return a.type === 'county' && a.county === lookupCounty && a.state === lookupState;
      });
      if (countyArea) {
        setFeesFromArea(countyArea);
      }
    }
    
    // Priority 4: Fall back to state coverage
    if (baseFee === null) {
      var checkState = lookupState || serviceState;
      if (checkState) {
        // First check for any county in this state
        var anyCountyArea = coverageAreas.find(function(a) {
          return a.type === 'county' && a.state === checkState;
        });
        if (anyCountyArea) {
          setFeesFromArea(anyCountyArea);
        } else {
          // Fall back to statewide
          var stateArea = coverageAreas.find(function(a) {
            return a.type === 'state' && a.state === checkState;
          });
          if (stateArea) {
            setFeesFromArea(stateArea);
          }
        }
      }
    }
    
    // If no match found, use minimum fee from all coverage areas
    if (baseFee === null) {
      var fees = coverageAreas.map(function(a) { return a.base_fee || 75; });
      baseFee = fees.length > 0 ? Math.min.apply(null, fees) : 75;
    }
    
    // Use area-specific rush/same-day fees if available, otherwise fall back to default
    var rushFee = areaRushFee != null ? areaRushFee : defaultRushFee;
    var sameDayFee = areaSameDayFee != null ? areaSameDayFee : defaultSameDayFee;
    
    // Build itemized breakdown
    var breakdown = [];
    var total = 0;
    
    // Base service fee
    var baseFeeTotal = baseFee * quoteForm.num_serves;
    total = total + baseFeeTotal;
    breakdown.push({
      label: quoteForm.num_serves > 1 ? 'Base Service Fee (' + quoteForm.num_serves + ' serves)' : 'Base Service Fee',
      amount: baseFeeTotal
    });
    
    // Urgency fees
    if (quoteForm.urgency === 'rush' && rushFee != null) {
      var rushTotal = rushFee * quoteForm.num_serves;
      total = total + rushTotal;
      breakdown.push({ label: 'Rush Fee (' + defaultPricing.rush_hours + 'h)', amount: rushTotal });
    }
    if (quoteForm.urgency === 'same_day' && sameDayFee != null) {
      var sameDayTotal = sameDayFee * quoteForm.num_serves;
      total = total + sameDayTotal;
      breakdown.push({ label: 'Same Day Fee', amount: sameDayTotal });
    }
    
    // Mileage fee - now uses billable miles (roundtrip - free miles)
    var mileageRate = defaultPricing.mileage_rate;
    var freeMiles = defaultPricing.free_miles || 0;
    var estimatedMiles = parseFloat(quoteForm.estimated_miles) || 0;
    // Only add mileage fee if there are billable miles (roundtrip exceeds free miles)
    if (mileageRate != null && estimatedMiles > freeMiles) {
      var chargeableMiles = estimatedMiles - freeMiles;
      var mileageFee = chargeableMiles * mileageRate;
      total = total + mileageFee;
      breakdown.push({ 
        label: 'Mileage (' + chargeableMiles.toFixed(1) + ' mi @ $' + mileageRate + '/mi)', 
        amount: mileageFee,
        detail: 'Total: ' + estimatedMiles.toFixed(1) + ' mi, ' + freeMiles + ' mi free'
      });
    }
    
    // Printing fee - input is "pages per packet", total = pages_per_packet × num_serves
    var printingConfig = server.printing_config || {};
    var freePagesPerServe = printingConfig.free_pages || 0;
    var perPageFee = printingConfig.per_page_fee || 0;
    var pagesPerPacket = parseInt(quoteForm.pages_to_print) || 0;
    var numServes = parseInt(quoteForm.num_serves) || 1;
    var totalPages = pagesPerPacket * numServes;
    var totalFreePages = freePagesPerServe * numServes;
    // Only add printing fee if total pages exceed free allocation
    if (printingConfig.enabled && totalPages > totalFreePages) {
      var chargeablePages = totalPages - totalFreePages;
      var printingFee = chargeablePages * perPageFee;
      total = total + printingFee;
      var printLabel = 'Printing (' + chargeablePages + ' pages @ $' + perPageFee + '/page)';
      breakdown.push({ 
        label: printLabel, 
        amount: printingFee,
        detail: totalPages + ' total pages (' + pagesPerPacket + '/packet × ' + numServes + '), ' + totalFreePages + ' free'
      });
    }
    
    // Extra attempts fee
    var additionalAttemptFee = defaultPricing.additional_attempt_fee;
    var extraAttempts = parseInt(quoteForm.extra_attempts) || 0;
    if (additionalAttemptFee != null && extraAttempts > 0) {
      var attemptsFee = extraAttempts * additionalAttemptFee;
      total = total + attemptsFee;
      breakdown.push({ label: 'Extra Attempts (' + extraAttempts + ' @ $' + additionalAttemptFee + ' each)', amount: attemptsFee });
    }
    
    return { total: total, breakdown: breakdown };
  }

  function updateQuoteField(field, value) {
    var newForm = Object.assign({}, quoteForm);
    newForm[field] = value;
    setQuoteForm(newForm);
    
    // If ZIP code changed, lookup city/county
    if (field === 'service_zip' && value && value.length === 5) {
      api.lookupZip(value).then(function(res) {
        if (res.data && res.data.found) {
          setZipLookup(res.data);
          // Auto-fill city and state if empty
          if (!quoteForm.service_city && res.data.city) {
            setQuoteForm(function(prev) {
              return Object.assign({}, prev, { 
                service_city: res.data.city,
                service_state: res.data.state,
                service_zip: value
              });
            });
          }
        }
      }).catch(function() {
        setZipLookup(null);
      });
    }
    
    // If city changed, clear ZIP lookup so estimate recalculates based on typed city
    if (field === 'service_city') {
      // Don't clear if this is a result of ZIP auto-fill
      if (zipLookup && zipLookup.city !== value) {
        setZipLookup(null);
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!server) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Server Not Found</h2>
            <Button asChild className="btn-secondary">
              <Link to="/search">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Search
              </Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  var trustScoreOverall = server.trust_score ? server.trust_score.overall : 50;
  var dataMaturity = server.trust_score ? server.trust_score.data_maturity : 'new';
  var baseFeeDisplay = server.pricing ? server.pricing.base_fee : 75;
  var rushFeeDisplay = server.pricing ? server.pricing.rush_fee_48h : 35;
  var sameDayDisplay = server.pricing ? server.pricing.same_day_fee : 75;
  var estimatedTotal = calculateEstimate();

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]">
      <Navbar />
      <main className="flex-1" data-testid="server-profile-page">
        {/* Hero band — editorial cobalt mesh / aurora */}
        <section className="relative overflow-hidden isolate pt-8 pb-2">
          <div className="absolute inset-0 dark:hidden pointer-events-none" style={{
            background:
              'radial-gradient(800px 360px at 20% 0%, hsl(226 100% 55% / 0.08), transparent 60%),' +
              'radial-gradient(700px 380px at 95% 10%, hsl(270 95% 60% / 0.05), transparent 60%)',
          }} />
          <div className="absolute inset-0 hidden dark:block pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 50% 0%, hsl(226 100% 55% / 0.10), transparent 70%)',
          }} />
          <div className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 pt-4">
            <Link to="/search" className="inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.18em] uppercase text-slate-500 dark:text-[hsl(220,12%,60%)] hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)] transition-colors">
              <ArrowLeft className="h-3 w-3" /> Back to Directory
            </Link>
          </div>
        </section>

        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-6 sm:py-8">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <ProfileHeader 
                server={server} 
                trustScore={trustScoreOverall}
                dataMaturity={dataMaturity}
                distanceMiles={server.distance_miles}
                searchState={fromState}
              />
              <CoverageAreas 
                areas={server.coverage_areas} 
                serverAddress={server.server_address} 
                satelliteOffices={server.satellite_offices}
                mapRef={mapRef}
                onAreaSelect={setSelectedArea}
                selectedArea={selectedArea}
              />
              <ReviewsList reviews={reviews} reviewCount={server.review_count} />
            </div>
            <div className="lg:sticky lg:top-24 space-y-6 self-start">
              <QuoteCard
                server={server}
                quoteOpen={quoteOpen}
                setQuoteOpen={setQuoteOpen}
                quoteForm={quoteForm}
                setQuoteForm={setQuoteForm}
                updateQuoteField={updateQuoteField}
                handleQuoteSubmit={handleQuoteSubmit}
                submitting={submitting}
                serverName={server.display_name}
                estimatedTotal={calculateEstimate()}
                searchState={fromState}
                selectedArea={selectedArea}
                mileageCalc={mileageCalc}
                verifiedCoords={verifiedCoords}
                setVerifiedCoords={setVerifiedCoords}
                coverageCheck={coverageCheck}
              />
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] p-5" data-testid="trust-breakdown">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-3">◢ SERVIO SCORE™</div>
                <TrustScoreBreakdown trustScore={server.trust_score} />
              </div>
              <AdditionalServicesCompact services={server.additional_services} />
              <MultiServeDiscount discount={server.multi_serve_discount} />
              
              {/* Report Button */}
              <div className="pt-4 border-t border-slate-200 dark:border-white/[0.06]">
                <button
                  onClick={function() { setComplaintOpen(true); }}
                  className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.14em] uppercase text-slate-500 dark:text-[hsl(220,12%,55%)] hover:text-[hsl(348,83%,55%)] dark:hover:text-[hsl(348,83%,62%)] transition-colors"
                  data-testid="report-server-btn"
                >
                  <Flag className="h-3.5 w-3.5" />
                  Report this server
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      
      {/* Complaint Dialog */}
      <ComplaintDialog
        open={complaintOpen}
        onOpenChange={setComplaintOpen}
        serverId={server.id}
        serverName={server.display_name}
      />
    </div>
  );
}

// Complaint Dialog Component
function ComplaintDialog(props) {
  var open = props.open;
  var onOpenChange = props.onOpenChange;
  var serverId = props.serverId;
  var serverName = props.serverName;
  
  var stepState = useState(1); // 1: form, 2: evidence upload, 3: success
  var step = stepState[0];
  var setStep = stepState[1];
  
  var formState = useState({
    category: '',
    incident_date: '',
    details: '',
    desired_resolution: ''
  });
  var form = formState[0];
  var setForm = formState[1];
  
  var submittingState = useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];
  
  var complaintIdState = useState(null);
  var complaintId = complaintIdState[0];
  var setComplaintId = complaintIdState[1];
  
  var evidenceFilesState = useState([]);
  var evidenceFiles = evidenceFilesState[0];
  var setEvidenceFiles = evidenceFilesState[1];
  
  var uploadingState = useState(false);
  var uploading = uploadingState[0];
  var setUploading = uploadingState[1];
  
  var categoriesState = useState({});
  var categories = categoriesState[0];
  var setCategories = categoriesState[1];
  
  // Load categories on mount
  useEffect(function() {
    api.getComplaintCategories().then(function(res) {
      setCategories(res.data.categories || {});
    }).catch(function() {});
  }, []);
  
  function handleSubmit(e) {
    e.preventDefault();
    
    // Check if logged in
    var token = localStorage.getItem('servio_token');
    if (!token) {
      toast.error('Please log in to file a complaint');
      return;
    }
    
    setSubmitting(true);
    api.submitComplaint({
      accused_server_id: serverId,
      category: form.category,
      incident_date: form.incident_date,
      details: form.details,
      desired_resolution: form.desired_resolution || null
    }).then(function(res) {
      setComplaintId(res.data.complaint_id);
      setStep(2);
      toast.success('Complaint submitted');
    }).catch(function(err) {
      var msg = err.response?.data?.detail || 'Failed to submit complaint';
      toast.error(msg);
    }).finally(function() {
      setSubmitting(false);
    });
  }
  
  function handleFileUpload(e) {
    var file = e.target.files && e.target.files[0];
    if (!file || !complaintId) return;
    
    setUploading(true);
    api.uploadComplaintEvidence(complaintId, file).then(function(res) {
      setEvidenceFiles(function(prev) {
        return prev.concat([{ file_id: res.data.file_id, file_name: file.name, file_type: file.type }]);
      });
      toast.success('Evidence uploaded');
    }).catch(function(err) {
      var msg = err.response?.data?.detail || 'Failed to upload file';
      toast.error(msg);
    }).finally(function() {
      setUploading(false);
      e.target.value = '';
    });
  }
  
  function handleDeleteFile(fileId) {
    api.deleteComplaintEvidence(complaintId, fileId).then(function() {
      setEvidenceFiles(function(prev) {
        return prev.filter(function(f) { return f.file_id !== fileId; });
      });
      toast.success('File removed');
    }).catch(function() {
      toast.error('Failed to remove file');
    });
  }
  
  function handleFinish() {
    setStep(3);
  }
  
  function handleClose() {
    onOpenChange(false);
    // Reset after close animation
    setTimeout(function() {
      setStep(1);
      setForm({ category: '', incident_date: '', details: '', desired_resolution: '' });
      setComplaintId(null);
      setEvidenceFiles([]);
    }, 300);
  }
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            {step === 3 ? 'Complaint Submitted' : 'Report ' + serverName}
          </DialogTitle>
        </DialogHeader>
        
        {step === 1 && (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="complaint-category" className="text-slate-300">Category *</Label>
              <Select value={form.category} onValueChange={function(v) { setForm(Object.assign({}, form, { category: v })); }}>
                <SelectTrigger id="complaint-category" className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select violation type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {Object.keys(categories).map(function(key) {
                    var cat = categories[key];
                    return (
                      <SelectItem key={key} value={key} className="text-slate-300 focus:bg-slate-800 focus:text-white">
                        {cat.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {form.category && categories[form.category] && (
                <p className="text-xs text-slate-500 mt-1">{categories[form.category].description}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="complaint-incident-date" className="text-slate-300">Incident Date *</Label>
              <Input
                id="complaint-incident-date"
                name="incident_date"
                type="date"
                required
                value={form.incident_date}
                onChange={function(e) { setForm(Object.assign({}, form, { incident_date: e.target.value })); }}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="complaint-details" className="text-slate-300">Details *</Label>
              <Textarea
                id="complaint-details"
                name="details"
                required
                rows={4}
                placeholder="Describe the incident in detail..."
                value={form.details}
                onChange={function(e) { setForm(Object.assign({}, form, { details: e.target.value })); }}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            
            <div>
              <Label htmlFor="complaint-resolution" className="text-slate-300">Desired Resolution</Label>
              <Textarea
                id="complaint-resolution"
                name="desired_resolution"
                rows={2}
                placeholder="What outcome would you like to see? (optional)"
                value={form.desired_resolution}
                onChange={function(e) { setForm(Object.assign({}, form, { desired_resolution: e.target.value })); }}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-xs text-amber-400">
                <strong>Note:</strong> Filing false complaints may result in penalties to your account. All complaints are reviewed by our team.
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={handleClose} className="border-slate-700">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !form.category || !form.incident_date || !form.details} className="bg-red-500 hover:bg-red-600 text-white">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit Complaint
              </Button>
            </div>
          </form>
        )}
        
        {step === 2 && (
          <div className="space-y-4 mt-4">
            <p className="text-slate-400">Upload supporting evidence (up to 5 files). PDFs or images accepted.</p>
            
            <div className="space-y-2">
              {evidenceFiles.map(function(file) {
                return (
                  <div key={file.file_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                    <span className="text-sm text-slate-300 truncate flex-1">{file.file_name}</span>
                    <button onClick={function() { handleDeleteFile(file.file_id); }} className="ml-2 text-slate-400 hover:text-red-400">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
            
            {evidenceFiles.length < 5 && (
              <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-700 rounded-lg cursor-pointer hover:border-slate-600 transition-colors">
                <Upload className="h-8 w-8 text-slate-500 mb-2" />
                <span className="text-sm text-slate-400">{uploading ? 'Uploading...' : 'Click to upload evidence'}</span>
                <span className="text-xs text-slate-500 mt-1">PDF, JPEG, PNG, or WebP (max 10MB)</span>
                <input type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.webp" disabled={uploading} />
              </label>
            )}
            
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={handleFinish} className="border-slate-700">
                {evidenceFiles.length === 0 ? 'Skip' : 'Done'}
              </Button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Flag className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Complaint Filed Successfully</h3>
            <p className="text-slate-400 mb-6">
              Our team will review your complaint and the server will be notified to provide their response.
              You&apos;ll receive updates via email and notifications.
            </p>
            <Button onClick={handleClose} className="btn-accent">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileHeader(props) {
  var server = props.server;
  var trustScore = props.trustScore;
  var dataMaturity = props.dataMaturity;
  var distanceMiles = props.distanceMiles;
  var searchState = props.searchState ? props.searchState.toUpperCase() : null;
  var serifStyle = { fontFamily: '"Instrument Serif"' };
  
  // Helper to get contextual minimum price
  function getContextualMinPrice(coverageAreas, state) {
    var filteredAreas = state 
      ? coverageAreas.filter(function(a) { return a.state === state; })
      : coverageAreas;
    if (filteredAreas.length === 0) filteredAreas = coverageAreas;
    var fees = filteredAreas.map(function(a) { return a.base_fee || 75; });
    return fees.length > 0 ? Math.min.apply(null, fees) : 75;
  }

  var contextualMin = (server.coverage_areas && server.coverage_areas.length > 0)
    ? getContextualMinPrice(server.coverage_areas, searchState)
    : null;

  return (
    <div className={'relative rounded-3xl border bg-white dark:bg-[hsl(220,30%,8%)] border-slate-200 dark:border-white/10 overflow-hidden ' + (server.is_founding_member ? 'founding-member-card' : '')} data-testid="profile-header">
      {/* Cobalt accent edge */}
      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[hsl(226,100%,55%)]/60 to-transparent" />
      {/* Soft cobalt corner glow */}
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(226 100% 55% / 0.10), transparent 70%)' }} />

      <div className="relative p-5 sm:p-7 md:p-8">
        {/* Eyebrow row */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]">◢ VERIFIED PROFILE</div>
          {server.created_at && (
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 dark:text-[hsl(220,12%,55%)] flex items-center gap-1.5">
              <Calendar className="h-3 w-3" />
              Member since {new Date(server.created_at).getFullYear()}
            </div>
          )}
        </div>

        {/* Mobile: Horizontal layout for photo, score, badge */}
        <div className="flex items-center gap-3 sm:hidden mb-4">
          {server.photo_url ? (
            <img src={server.photo_url} alt={server.display_name} className="w-14 h-14 rounded-2xl object-cover border border-slate-200 dark:border-white/10 flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(226,100%,55%)] to-[hsl(270,80%,55%)] flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
              {server.display_name ? server.display_name.charAt(0).toUpperCase() : 'S'}
            </div>
          )}
          <TrustScoreRing score={trustScore} size="sm" />
          <DataMaturityBadge maturity={dataMaturity} />
        </div>

        {/* Mobile: Name and badges */}
        <div className="sm:hidden mb-4">
          <h1 className="text-2xl font-bold tracking-[-0.02em]">
            {server.display_name}
          </h1>
          {server.company_name && <p className="text-sm italic text-slate-500 dark:text-[hsl(220,12%,60%)] mt-0.5" style={serifStyle}>{server.company_name}</p>}
          <div className="mt-3">
            <BadgeList
              verifications={server.verifications}
              yearsExperience={server.years_experience}
              responseGuarantee={server.response_guarantee_hours}
              isFoundingMember={server.is_founding_member}
              stateCertifications={server.state_certifications || []}
            />
          </div>
        </div>

        {/* Desktop: Editorial split layout */}
        <div className="hidden sm:flex flex-row gap-7 items-start">
          <div className="flex-shrink-0 flex flex-col items-center gap-3">
            {server.photo_url ? (
              <img src={server.photo_url} alt={server.display_name} className="w-20 h-20 rounded-2xl object-cover border border-slate-200 dark:border-white/10 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)]" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[hsl(226,100%,55%)] to-[hsl(270,80%,55%)] flex items-center justify-center text-white text-2xl font-bold shadow-[0_12px_36px_-10px_hsl(226,100%,55%)]">
                {server.display_name ? server.display_name.charAt(0).toUpperCase() : 'S'}
              </div>
            )}
            <TrustScoreRing score={trustScore} size="sm" />
            <DataMaturityBadge maturity={dataMaturity} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold tracking-[-0.035em] leading-[1.05]" style={{ fontSize: 'clamp(1.6rem, 2.4vw, 2.2rem)' }}>
              {server.display_name}
            </h1>
            {server.company_name && (
              <p className="mt-0.5 text-lg italic text-slate-500 dark:text-[hsl(220,12%,62%)]" style={serifStyle}>
                {server.company_name}
              </p>
            )}
            <div className="mt-4">
              <BadgeList
                verifications={server.verifications}
                yearsExperience={server.years_experience}
                responseGuarantee={server.response_guarantee_hours}
                isFoundingMember={server.is_founding_member}
                stateCertifications={server.state_certifications || []}
              />
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-600 dark:text-[hsl(220,12%,72%)]">
              {server.phone ? (
                <a href={'tel:' + server.phone} className="flex items-center gap-2 hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)] transition-colors">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>{server.phone}</span>
                </a>
              ) : <div />}
              {server.email ? (
                <a href={'mailto:' + server.email} className="flex items-center gap-2 hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)] transition-colors truncate">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{server.email}</span>
                </a>
              ) : <div />}
              {server.server_address && server.server_address.city ? (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col">
                    {server.server_address.street && <span>{server.server_address.street}</span>}
                    <span>{server.server_address.city}, {server.server_address.state} {server.server_address.zip_code}</span>
                  </div>
                </div>
              ) : <div />}
              {server.website ? (
                <a href={server.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)] transition-colors truncate">
                  <Globe className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{server.website}</span>
                </a>
              ) : <div />}
            </div>

            {/* KPI strip — editorial mono */}
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-mono tracking-[0.12em] uppercase">
              {distanceMiles != null && (
                <span className="inline-flex items-center gap-1.5 text-[hsl(190,95%,40%)] dark:text-[hsl(190,95%,60%)]">
                  <Navigation className="h-3.5 w-3.5" />
                  <span className="tabular-nums">~{distanceMiles} mi</span>
                </span>
              )}
              {server.response_guarantee_hours && (
                <span className="inline-flex items-center gap-1.5 text-[hsl(270,80%,50%)] dark:text-[hsl(270,80%,68%)]">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="tabular-nums">{server.response_guarantee_hours}h response</span>
                </span>
              )}
              {contextualMin != null && (
                <span className="inline-flex items-center gap-1.5 text-[hsl(150,80%,38%)] dark:text-[hsl(150,80%,60%)]">
                  <DollarSign className="h-3.5 w-3.5" />
                  <span className="tabular-nums">From ${contextualMin}</span>
                </span>
              )}
              {server.review_count > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[hsl(38,95%,45%)] dark:text-[hsl(38,95%,62%)]">
                  <Star className="h-3.5 w-3.5 fill-current" />
                  <span className="tabular-nums">{server.average_rating} · {server.review_count} reviews</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Mobile: Contact info and stats */}
        <div className="sm:hidden">
          <div className="space-y-2 text-sm text-slate-600 dark:text-[hsl(220,12%,72%)] mb-3">
            {server.phone && (
              <a href={'tel:' + server.phone} className="flex items-center gap-2 hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)]">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <span>{server.phone}</span>
              </a>
            )}
            {server.email && (
              <a href={'mailto:' + server.email} className="flex items-center gap-2 hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)]">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <span className="break-all">{server.email}</span>
              </a>
            )}
            {server.website && (
              <a href={server.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)]">
                <Globe className="h-4 w-4 flex-shrink-0" />
                <span className="break-all">{server.website}</span>
              </a>
            )}
            {server.server_address && server.server_address.city && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  {server.server_address.street && <span>{server.server_address.street}</span>}
                  <span>{server.server_address.city}, {server.server_address.state} {server.server_address.zip_code}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] font-mono tracking-[0.12em] uppercase">
            {distanceMiles != null && (
              <span className="inline-flex items-center gap-1 text-[hsl(190,95%,40%)] dark:text-[hsl(190,95%,60%)]">
                <Navigation className="h-3 w-3" />
                ~{distanceMiles} mi
              </span>
            )}
            {server.response_guarantee_hours && (
              <span className="inline-flex items-center gap-1 text-[hsl(270,80%,50%)] dark:text-[hsl(270,80%,68%)]">
                <Clock className="h-3 w-3" />
                {server.response_guarantee_hours}h
              </span>
            )}
            {contextualMin != null && (
              <span className="inline-flex items-center gap-1 text-[hsl(150,80%,38%)] dark:text-[hsl(150,80%,60%)]">
                <DollarSign className="h-3 w-3" />
                From ${contextualMin}
              </span>
            )}
            {server.review_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[hsl(38,95%,45%)] dark:text-[hsl(38,95%,62%)]">
                <Star className="h-3 w-3 fill-current" />
                {server.average_rating} ({server.review_count})
              </span>
            )}
          </div>
        </div>

        {server.bio && (
          <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-slate-200 dark:border-white/[0.06]">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 dark:text-[hsl(220,12%,55%)] mb-2">◢ ABOUT</div>
            <p className="text-[15px] text-slate-700 dark:text-[hsl(220,12%,80%)] leading-relaxed">{server.bio}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CoverageAreas(props) {
  var areas = props.areas || [];
  var serverAddress = props.serverAddress;
  var satelliteOffices = props.satelliteOffices || [];
  var mapRef = props.mapRef;
  var onAreaSelect = props.onAreaSelect;
  var selectedArea = props.selectedArea;
  
  var expandedState = React.useState(false);
  var expanded = expandedState[0];
  var setExpanded = expandedState[1];
  
  var showMapState = React.useState(true);
  var showMap = showMapState[0];
  var setShowMap = showMapState[1];
  
  if (areas.length === 0) return null;
  
  // State name mapping - all 50 states
  var stateNames = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'Washington D.C.'
  };
  function getStateName(code) { return stateNames[code] || code; }
  
  // Helper to check if area is selected
  function isAreaSelected(area) {
    if (!selectedArea) return false;
    if (area.type === 'state' && selectedArea.type === 'state') {
      return area.state === selectedArea.state;
    }
    if (area.county && selectedArea.county) {
      return area.county === selectedArea.county && area.state === selectedArea.state;
    }
    if (area.city && selectedArea.city) {
      return area.city === selectedArea.city && area.state === selectedArea.state;
    }
    return false;
  }
  
  // Zoom handlers - now also select the area
  function handleCountyClick(county, state, area) {
    if (onAreaSelect) onAreaSelect(area);
    if (mapRef && mapRef.current) {
      setShowMap(true);
      setTimeout(function() {
        mapRef.current.zoomToCounty(county, state);
      }, 100);
    }
  }
  
  function handleZipClick(zipCode, area) {
    // Create a modified area object with just the clicked ZIP
    var selectedZipArea = Object.assign({}, area, { 
      zip_codes: [zipCode],
      _clickedZip: zipCode 
    });
    if (onAreaSelect) onAreaSelect(selectedZipArea);
    if (mapRef && mapRef.current) {
      setShowMap(true);
      setTimeout(function() {
        mapRef.current.zoomToZip(zipCode);
      }, 100);
    }
  }
  
  function handleStateClick(state, area) {
    if (onAreaSelect) onAreaSelect(area);
    if (mapRef && mapRef.current) {
      setShowMap(true);
      setTimeout(function() {
        mapRef.current.zoomToState(state);
      }, 100);
    }
  }
  
  function handleCityClick(city, state, area) {
    if (onAreaSelect) onAreaSelect(area);
    if (mapRef && mapRef.current) {
      setShowMap(true);
      setTimeout(function() {
        mapRef.current.zoomToCity(city, state);
      }, 100);
    }
  }
  
  function handleResetView() {
    if (onAreaSelect) onAreaSelect(null);
    if (mapRef && mapRef.current) {
      mapRef.current.resetView();
    }
  }
  
  // Group areas by type and sort alphabetically
  var stateAreas = areas.filter(function(a) { return a.type === 'state'; }).sort(function(a, b) {
    return (getStateName(a.state) || '').localeCompare(getStateName(b.state) || '');
  });
  var countyAreas = areas.filter(function(a) { return a.type === 'county' || a.county; }).sort(function(a, b) {
    var aName = (a.county || '') + ', ' + (a.state || '');
    var bName = (b.county || '') + ', ' + (b.state || '');
    return aName.localeCompare(bName);
  });
  var cityAreas = areas.filter(function(a) { return a.type === 'city' || a.city; }).sort(function(a, b) {
    var aName = (a.city || '') + ', ' + (a.state || '');
    var bName = (b.city || '') + ', ' + (b.state || '');
    return aName.localeCompare(bName);
  });
  var zipAreas = areas.filter(function(a) { return a.type === 'zip' || (a.zip_codes && a.zip_codes.length > 0); });
  
  var totalZips = zipAreas.reduce(function(sum, a) { return sum + (a.zip_codes ? a.zip_codes.length : 0); }, 0);
  
  return (
    <Card className="card-base" data-testid="coverage-areas">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-white">
          <span className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-teal-400" />
            Coverage Areas
          </span>
          <div className="flex items-center gap-3">
            <span className="text-sm font-normal text-slate-400">
              {stateAreas.length > 0 && stateAreas.length + ' State' + (stateAreas.length > 1 ? 's' : '')}
              {countyAreas.length > 0 && (stateAreas.length > 0 ? ' • ' : '') + countyAreas.length + ' Count' + (countyAreas.length > 1 ? 'ies' : 'y')}
              {cityAreas.length > 0 && ((stateAreas.length > 0 || countyAreas.length > 0) ? ' • ' : '') + cityAreas.length + ' Cit' + (cityAreas.length > 1 ? 'ies' : 'y')}
              {totalZips > 0 && ((stateAreas.length > 0 || countyAreas.length > 0 || cityAreas.length > 0) ? ' • ' : '') + totalZips + ' ZIP' + (totalZips > 1 ? 's' : '')}
            </span>
            <button 
              onClick={function() { setShowMap(!showMap); }} 
              className={'p-1.5 rounded-md transition-colors ' + (showMap ? 'bg-teal-500/20 text-teal-400' : 'text-slate-500 hover:text-slate-300')}
              title={showMap ? 'Hide map' : 'Show map'}
            >
              <Map className="h-4 w-4" />
            </button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {showMap && (
          <div className="mb-4 relative">
            <Suspense fallback={<div className="h-[250px] bg-slate-800 rounded-lg flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>}>
              <CoverageMap ref={mapRef} coverageAreas={areas} serverAddress={serverAddress} satelliteOffices={satelliteOffices} height="250px" />
            </Suspense>
            <button
              onClick={handleResetView}
              className="absolute top-2 right-2 p-1.5 bg-slate-900/80 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Reset view"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
        )}
        <p className="text-xs text-slate-500 mb-3">Click any area below to zoom on map</p>
        <div className="space-y-4">
          {stateAreas.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Statewide Coverage</p>
              <div className="flex flex-wrap gap-2">
                {stateAreas.map(function(area, i) {
                  var attempts = area.attempts_included || 3;
                  var pricingType = area.pricing_type || 'fixed';
                  var isSelected = isAreaSelected(area);
                  var isContactForQuote = pricingType === 'call_for_quote';
                  
                  return (
                    <button 
                      key={i} 
                      onClick={function() { handleStateClick(area.state, area); }}
                      className={'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ' + 
                        (isSelected 
                          ? 'bg-violet-500/30 border-2 border-violet-400 ring-2 ring-violet-400/30' 
                          : 'bg-violet-500/10 border border-violet-500/30 hover:bg-violet-500/20 hover:border-violet-400')}
                    >
                      <span className="text-sm font-medium text-violet-400">{getStateName(area.state)}</span>
                      {isContactForQuote && (
                        <span className="flex items-center gap-1 text-xs text-amber-400">
                          <Phone className="h-3 w-3" />
                          Contact
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {countyAreas.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Counties</p>
              <div className="flex flex-wrap gap-2">
                {countyAreas.slice(0, expanded ? countyAreas.length : 6).map(function(area, i) {
                  var isSelected = isAreaSelected(area);
                  return (
                    <button 
                      key={i} 
                      onClick={function() { handleCountyClick(area.county, area.state, area); }}
                      className={'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ' +
                        (isSelected
                          ? 'bg-cyan-500/30 border-2 border-cyan-400 ring-2 ring-cyan-400/30'
                          : 'bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:border-cyan-400')}
                    >
                      <span className="text-sm text-cyan-300">{area.county} County, {getStateName(area.state)}</span>
                    </button>
                  );
                })}
                {countyAreas.length > 6 && !expanded && (
                  <button onClick={function() { setExpanded(true); }} className="px-3 py-2 text-sm text-cyan-400 hover:text-cyan-300">+{countyAreas.length - 6} more</button>
                )}
              </div>
            </div>
          )}
          
          {cityAreas.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Cities</p>
              <div className="flex flex-wrap gap-2">
                {cityAreas.slice(0, expanded ? cityAreas.length : 6).map(function(area, i) {
                  var isSelected = isAreaSelected(area);
                  var pricingType = area.pricing_type || 'fixed';
                  var isContactForQuote = pricingType === 'call_for_quote';
                  return (
                    <button 
                      key={i} 
                      onClick={function() { handleCityClick(area.city, area.state, area); }}
                      className={'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ' +
                        (isSelected
                          ? 'bg-emerald-500/30 border-2 border-emerald-400 ring-2 ring-emerald-400/30'
                          : 'bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-400')}
                    >
                      <span className="text-sm text-emerald-300">{area.city}, {getStateName(area.state)}</span>
                      {isContactForQuote && (
                        <span className="flex items-center gap-1 text-xs text-amber-400">
                          <Phone className="h-3 w-3" />
                          Contact
                        </span>
                      )}
                    </button>
                  );
                })}
                {cityAreas.length > 6 && !expanded && (
                  <button onClick={function() { setExpanded(true); }} className="px-3 py-2 text-sm text-emerald-400 hover:text-emerald-300">+{cityAreas.length - 6} more</button>
                )}
              </div>
            </div>
          )}
          
          {zipAreas.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Zip Code Areas</p>
              <div className="space-y-2">
                {zipAreas.slice(0, expanded ? zipAreas.length : 3).map(function(area, i) {
                  var zips = area.zip_codes || [];
                  var pricingType = area.pricing_type || 'fixed';
                  var isCallForQuote = pricingType === 'call_for_quote';
                  return (
                    <div key={i} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-sm text-amber-400">{getStateName(area.state)}</span>
                        {isCallForQuote && (
                          <span className="flex items-center gap-1 text-xs text-amber-400">
                            <Phone className="h-3 w-3" />
                            Contact
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {zips.map(function(zip, j) {
                          return (
                            <button
                              key={j}
                              onClick={function() { handleZipClick(zip, area); }}
                              className="px-2 py-1 text-xs font-mono bg-amber-500/20 text-amber-200 rounded hover:bg-amber-500/40 transition-colors"
                            >
                              {zip}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {zipAreas.length > 3 && !expanded && (
                  <button onClick={function() { setExpanded(true); }} className="text-sm text-amber-400 hover:text-amber-300">View all zip code areas</button>
                )}
              </div>
            </div>
          )}
          
          {expanded && (countyAreas.length > 6 || cityAreas.length > 6 || zipAreas.length > 3) && (
            <button onClick={function() { setExpanded(false); }} className="text-sm text-slate-400 hover:text-white">Show less</button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AdditionalServicesCompact(props) {
  var services = props.services || [];
  
  // Only show services that are enabled AND either don't require verification OR are verified
  var REQUIRES_VERIFICATION = ['notary', 'mobile_notary', 'private_investigator'];
  var displayServices = services.filter(function(s) { 
    if (!s.enabled) return false;
    // If service requires verification, only show if verified
    if (REQUIRES_VERIFICATION.includes(s.service_type)) {
      return s.verification_status === 'verified';
    }
    return true;
  });
  
  if (displayServices.length === 0) return null;
  
  return (
    <Card className="card-base" data-testid="additional-services">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-white">
          <Award className="h-4 w-4 text-teal-400" />
          Services
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {displayServices.map(function(service) {
            var SERVICE_ABBREV = {
              'process_serving': { abbrev: 'PS', name: 'Process Serving', color: 'bg-teal-500/20 text-teal-400 border-teal-500/30' },
              'notary': { abbrev: 'N', name: 'Notary Public', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
              'mobile_notary': { abbrev: 'MN', name: 'Mobile Notary', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
              'private_investigator': { abbrev: 'PI', name: 'Private Investigator', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
              'court_filings': { abbrev: 'CF', name: 'Court Filings', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
              'court_records_search': { abbrev: 'CRS', name: 'Court Records Search', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
              'subpoena_preparation': { abbrev: 'SP', name: 'Subpoena Preparation', color: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
              'skip_tracing': { abbrev: 'ST', name: 'Skip Tracing', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
            };
            var info = SERVICE_ABBREV[service.service_type];
            if (!info) return null;
            var isVerified = REQUIRES_VERIFICATION.includes(service.service_type) && service.verification_status === 'verified';
            return (
              <div key={service.service_type} className="flex items-center gap-2 py-1.5 px-2 rounded bg-slate-800/30">
                <span className={'px-1.5 py-0.5 text-xs font-bold rounded border ' + info.color}>{info.abbrev}</span>
                <span className="text-sm text-slate-300">{info.name}</span>
                {isVerified && (
                  <span className="ml-auto flex items-center gap-1 text-xs text-emerald-400">
                    <CheckCircle className="h-3 w-3" />
                    Verified
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-3">Contact server for pricing on additional services.</p>
      </CardContent>
    </Card>
  );
}

function MultiServeDiscount(props) {
  var discount = props.discount;
  
  if (!discount || !discount.enabled) return null;
  
  var discountText = discount.discount_type === 'percentage' 
    ? discount.discount_amount + '% off' 
    : '$' + discount.discount_amount + ' off';
  
  return (
    <Card className="card-base border-amber-500/30" data-testid="multi-serve-discount">
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <DollarSign className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-400">Multi-Serve Discount</p>
            <p className="text-xs text-slate-400">{discountText} additional papers at same address</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewsList(props) {
  var reviews = props.reviews || [];
  var reviewCount = props.reviewCount || 0;
  
  function renderStars(rating) {
    var stars = [];
    for (var i = 0; i < 5; i++) {
      var starClass = i < rating ? 'h-4 w-4 text-amber-400 fill-amber-400' : 'h-4 w-4 text-slate-700';
      stars.push(<Star key={i} className={starClass} />);
    }
    return stars;
  }
  
  return (
    <Card className="card-base" data-testid="reviews-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Star className="h-5 w-5 text-amber-400" />
          Reviews ({reviewCount})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {reviews.length > 0 ? (
          <div className="space-y-6">
            {reviews.map(function(review) {
              return (
                <div key={review.id} className="pb-6 border-b border-slate-800 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-white">{review.reviewer_name}</p>
                      {review.reviewer_company && <p className="text-sm text-slate-500">{review.reviewer_company}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      {renderStars(review.overall_rating)}
                    </div>
                  </div>
                  {review.review_text && <p className="mt-2 text-slate-400">{review.review_text}</p>}
                  <p className="mt-2 text-xs text-slate-600">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">No reviews yet</p>
        )}
      </CardContent>
    </Card>
  );
}

function QuoteCard(props) {
  var server = props.server;
  var coverageAreas = server.coverage_areas || [];
  var defaultPricing = server.default_pricing || {};
  var searchState = props.searchState ? props.searchState.toUpperCase() : null;
  var selectedArea = props.selectedArea;
  
  // State name mapping - all 50 states
  var stateNames = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'Washington D.C.'
  };
  function getFullStateName(code) { return stateNames[code] || code; }
  
  // If a specific area is selected, use that for filtering
  // Otherwise, filter by searched state
  var filteredAreas;
  var displayAreaName = null;
  
  // Determine color theme based on selected area type
  var areaColorTheme = 'teal'; // default
  if (selectedArea) {
    if (selectedArea.type === 'state') {
      areaColorTheme = 'violet';
    } else if (selectedArea.county) {
      areaColorTheme = 'cyan';
    } else if (selectedArea.city) {
      areaColorTheme = 'emerald';
    } else if (selectedArea.zip_codes && selectedArea.zip_codes.length > 0) {
      areaColorTheme = 'amber';
    }
  }
  
  if (selectedArea) {
    // User clicked on a specific area - use only that area
    filteredAreas = [selectedArea];
    // Build display name for the selected area - use full state names
    if (selectedArea.type === 'state') {
      displayAreaName = getFullStateName(selectedArea.state);
    } else if (selectedArea.county) {
      displayAreaName = selectedArea.county + ' County, ' + getFullStateName(selectedArea.state);
    } else if (selectedArea.city) {
      displayAreaName = selectedArea.city + ', ' + getFullStateName(selectedArea.state);
    } else if (selectedArea._clickedZip) {
      // Use the specific clicked ZIP code
      displayAreaName = 'ZIP ' + selectedArea._clickedZip;
    } else if (selectedArea.zip_codes && selectedArea.zip_codes.length > 0) {
      displayAreaName = 'ZIP ' + selectedArea.zip_codes[0];
    }
  } else if (searchState) {
    filteredAreas = coverageAreas.filter(function(a) { return a.state === searchState; });
  } else {
    filteredAreas = coverageAreas;
  }
  
  // If no areas match, fall back to all areas
  if (filteredAreas.length === 0) filteredAreas = coverageAreas;
  
  // Check if ALL filtered areas are "call_for_quote" (hide quote form entirely)
  var allCallForQuote = filteredAreas.length > 0 && filteredAreas.every(function(a) { return a.pricing_type === 'call_for_quote'; });
  
  // Get attempts from selected area (only shown when not call_for_quote)
  var attemptsIncluded = selectedArea ? (selectedArea.attempts_included || 3) : null;
  
  // Get min/max base fees from filtered coverage areas (excluding call_for_quote)
  var pricedAreas = filteredAreas.filter(function(a) { return a.pricing_type !== 'call_for_quote'; });
  var fees = [];
  pricedAreas.forEach(function(a) {
    if (a.pricing_type === 'variable') {
      if (a.min_fee != null) fees.push(a.min_fee);
      if (a.max_fee != null) fees.push(a.max_fee);
    } else {
      fees.push(a.base_fee || 75);
    }
  });
  
  var minFee = fees.length > 0 ? Math.min.apply(null, fees) : 75;
  var maxFee = fees.length > 0 ? Math.max.apply(null, fees) : 75;
  var hasPriceRange = minFee !== maxFee;
  var hasAnyPricing = pricedAreas.length > 0;
  
  // Get rush/same day fees from FILTERED coverage areas (per-area fees)
  // Only show fees for the area(s) being displayed
  var rushFeesFromAreas = filteredAreas.filter(function(a) { return a.rush_fee != null; }).map(function(a) { return a.rush_fee; });
  var sameDayFeesFromAreas = filteredAreas.filter(function(a) { return a.same_day_fee != null; }).map(function(a) { return a.same_day_fee; });
  var rushHoursFromAreas = filteredAreas.filter(function(a) { return a.rush_hours != null; }).map(function(a) { return a.rush_hours; });
  
  var rushFee = rushFeesFromAreas.length > 0 ? Math.min.apply(null, rushFeesFromAreas) : null;
  var rushFeeMax = rushFeesFromAreas.length > 0 ? Math.max.apply(null, rushFeesFromAreas) : null;
  var rushHours = rushHoursFromAreas.length > 0 ? Math.min.apply(null, rushHoursFromAreas) : 48;
  var sameDayFee = sameDayFeesFromAreas.length > 0 ? Math.min.apply(null, sameDayFeesFromAreas) : null;
  var sameDayFeeMax = sameDayFeesFromAreas.length > 0 ? Math.max.apply(null, sameDayFeesFromAreas) : null;
  
  // Other fees from default pricing (mileage, additional attempt are still global)
  var mileageRate = defaultPricing.mileage_rate;
  var freeMiles = defaultPricing.free_miles;
  var additionalAttemptFee = defaultPricing.additional_attempt_fee;
  
  var hasRush = rushFee != null;
  var hasSameDay = sameDayFee != null;
  var hasRushRange = hasRush && rushFeeMax != null && rushFee !== rushFeeMax;
  var hasSameDayRange = hasSameDay && sameDayFeeMax != null && sameDayFee !== sameDayFeeMax;
  var hasMileage = mileageRate != null;
  var hasAdditionalAttempt = additionalAttemptFee != null;
  var hasAnyFees = hasRush || hasSameDay || hasMileage || hasAdditionalAttempt;
  
  // Printing config
  var printingConfig = server.printing_config || {};
  var hasPrinting = printingConfig.enabled;
  
  // If all areas for the selected/searched location are "Call for Quote", show call-only card
  if (allCallForQuote) {
    return (
      <Card className="card-base border-amber-500/30" data-testid="quote-cta">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Contact for Quote</h3>
          {displayAreaName && (
            <p className="text-sm text-amber-400 mb-3">{displayAreaName}</p>
          )}
          <div className="text-center py-4">
            <Phone className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <p className="text-slate-600 dark:text-slate-300 mb-4">Contact this server directly for pricing in this area.</p>
            <div className="flex flex-col gap-3">
              {server.phone && (
                <a 
                  href={'tel:' + server.phone} 
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-lg transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {server.phone}
                </a>
              )}
              {server.email && (
                <a 
                  href={'mailto:' + server.email} 
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg transition-colors border border-slate-300 dark:border-slate-600"
                >
                  <Mail className="h-4 w-4" />
                  {server.email}
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Color theme classes based on area type
  var colorClasses = {
    teal: { text: 'text-teal-400', border: 'border-teal-500/30', bg: 'bg-teal-500', hover: 'hover:bg-teal-400' },
    violet: { text: 'text-violet-400', border: 'border-violet-500/30', bg: 'bg-violet-500', hover: 'hover:bg-violet-400' },
    cyan: { text: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500', hover: 'hover:bg-cyan-400' },
    emerald: { text: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500', hover: 'hover:bg-emerald-400' },
    amber: { text: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500', hover: 'hover:bg-amber-400' }
  };
  var colors = colorClasses[areaColorTheme] || colorClasses.teal;
  
  return (
    <Card className={'card-base ' + (selectedArea ? colors.border : '')} data-testid="quote-cta">
      <CardContent className="p-6">
        <h3 className="font-semibold text-white mb-4">Request a Quote</h3>
        
        {/* Show selected area name if applicable */}
        {displayAreaName && (
          <p className={'text-sm mb-3 ' + colors.text}>{displayAreaName}</p>
        )}
        
        {/* Pricing by Area */}
        {hasAnyPricing && (
          <div className="mb-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
              {displayAreaName ? 'Base Fee' : 'Base Fee by Area'}
            </p>
            {hasPriceRange ? (
              <p className={'text-2xl font-bold font-mono ' + colors.text}>${minFee} - ${maxFee}</p>
            ) : (
              <p className={'text-2xl font-bold font-mono ' + colors.text}>${minFee}</p>
            )}
            {!displayAreaName && (
              <p className="text-xs text-slate-500 mt-1">Depends on service location</p>
            )}
            {attemptsIncluded && (
              <p className="text-xs text-slate-400 mt-1">Includes {attemptsIncluded} service attempts</p>
            )}
          </div>
        )}
        
        {/* Additional Fees - only show enabled ones */}
        {hasAnyFees && (
          <div className="space-y-2 py-4 border-t border-slate-800 text-sm">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Additional Fees</p>
            {hasRush && (
              <div className="flex justify-between">
                <span className="text-slate-400">Rush ({rushHours}h)</span>
                <span className="font-medium text-white font-mono">
                  +${rushFee}{hasRushRange ? ' - $' + rushFeeMax : ''}
                </span>
              </div>
            )}
            {hasSameDay && (
              <div className="flex justify-between">
                <span className="text-slate-400">Same Day</span>
                <span className="font-medium text-white font-mono">
                  +${sameDayFee}{hasSameDayRange ? ' - $' + sameDayFeeMax : ''}
                </span>
              </div>
            )}
            {hasMileage && (
              <div className="flex justify-between">
                <span className="text-slate-400">Mileage (after {freeMiles} mi)</span>
                <span className="font-medium text-white font-mono">${mileageRate}/mi</span>
              </div>
            )}
            {hasAdditionalAttempt && (
              <div className="flex justify-between">
                <span className="text-slate-400">Additional Attempt</span>
                <span className="font-medium text-white font-mono">+${additionalAttemptFee}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Printing */}
        {hasPrinting && (
          <div className="py-3 border-t border-slate-800 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Printing ({printingConfig.free_pages} free)</span>
              <span className="font-medium text-white font-mono">${printingConfig.per_page_fee}/page</span>
            </div>
          </div>
        )}

        <Dialog open={props.quoteOpen} onOpenChange={props.setQuoteOpen}>
          <DialogTrigger asChild>
            <Button className={'w-full text-slate-900 font-semibold ' + colors.bg + ' ' + colors.hover} data-testid="request-quote-btn">Request Quote</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Request Quote from {props.serverName}</DialogTitle>
            </DialogHeader>
            <QuoteForm
              server={server}
              quoteForm={props.quoteForm}
              setQuoteForm={props.setQuoteForm}
              updateQuoteField={props.updateQuoteField}
              handleQuoteSubmit={props.handleQuoteSubmit}
              submitting={props.submitting}
              estimatedTotal={props.estimatedTotal}
              hasRush={hasRush}
              hasSameDay={hasSameDay}
              rushHours={rushHours}
              rushFee={rushFee}
              sameDayFee={sameDayFee}
              mileageCalc={props.mileageCalc}
              verifiedCoords={props.verifiedCoords}
              setVerifiedCoords={props.setVerifiedCoords}
              coverageCheck={props.coverageCheck}
              serverName={props.serverName}
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function QuoteForm(props) {
  var form = props.quoteForm;
  var update = props.updateQuoteField;
  var hasRush = props.hasRush;
  var hasSameDay = props.hasSameDay;
  var rushHours = props.rushHours;
  var rushFee = props.rushFee;
  var sameDayFee = props.sameDayFee;
  var server = props.server;
  var mileageCalc = props.mileageCalc || {};
  var setVerifiedCoords = props.setVerifiedCoords;
  var coverageCheck = props.coverageCheck || {};
  var serverName = props.serverName || 'this server';
  
  // Address autocomplete state
  var addressSearchState = useState('');
  var addressSearch = addressSearchState[0];
  var setAddressSearch = addressSearchState[1];
  var suggestionsState = useState([]);
  var suggestions = suggestionsState[0];
  var setSuggestions = suggestionsState[1];
  var showSuggestionsState = useState(false);
  var showSuggestions = showSuggestionsState[0];
  var setShowSuggestions = showSuggestionsState[1];
  var loadingState = useState(false);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];
  var searchTimeoutRef = useRef(null);
  var containerRef = useRef(null);
  
  // Fetch address suggestions from Mapbox
  useEffect(function() {
    if (!addressSearch || addressSearch.length < 3) {
      setSuggestions([]);
      return;
    }
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce the search
    searchTimeoutRef.current = setTimeout(function() {
      setIsLoading(true);
      var MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
      var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURIComponent(addressSearch) + '.json?access_token=' + MAPBOX_TOKEN + '&country=us&types=address&limit=5&autocomplete=true';
      
      fetch(url)
        .then(function(response) { return response.json(); })
        .then(function(data) {
          if (data.features) {
            var formatted = data.features.map(function(feature) {
              var stateCode = null;
              var cityName = null;
              var zipCode = null;
              
              if (feature.context) {
                for (var i = 0; i < feature.context.length; i++) {
                  var ctx = feature.context[i];
                  if (ctx.id.startsWith('region')) {
                    stateCode = ctx.short_code ? ctx.short_code.replace('US-', '') : null;
                  }
                  if (ctx.id.startsWith('place')) {
                    cityName = ctx.text;
                  }
                  if (ctx.id.startsWith('postcode')) {
                    zipCode = ctx.text;
                  }
                }
              }
              
              // Extract street address from place_name (first part before first comma)
              var streetAddress = '';
              if (feature.place_name) {
                var parts = feature.place_name.split(',');
                if (parts.length > 0) {
                  streetAddress = parts[0].trim();
                }
              }
              // Fallback to feature.address + feature.text
              if (!streetAddress && feature.address) {
                streetAddress = feature.address + ' ' + feature.text;
              } else if (!streetAddress) {
                streetAddress = feature.text || '';
              }
              
              return {
                id: feature.id,
                fullAddress: feature.place_name,
                street: streetAddress,
                city: cityName,
                state: stateCode,
                zip: zipCode,
                lat: feature.center ? feature.center[1] : null,
                lng: feature.center ? feature.center[0] : null
              };
            });
            setSuggestions(formatted);
          }
          setIsLoading(false);
        })
        .catch(function() {
          setSuggestions([]);
          setIsLoading(false);
        });
    }, 300);
    
    return function() {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [addressSearch]);
  
  // Handle click outside to close suggestions
  useEffect(function() {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function() { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);
  
  // Handle address selection from autocomplete
  function handleAddressSelect(suggestion) {
    setAddressSearch(suggestion.fullAddress);
    setShowSuggestions(false);
    
    // Batch update all address fields at once to avoid stale closure issues
    var newForm = Object.assign({}, form, {
      service_street: suggestion.street || '',
      service_city: suggestion.city || '',
      service_state: suggestion.state || '',
      service_zip: suggestion.zip || ''
    });
    
    // Use the parent's setQuoteForm directly if available
    if (props.setQuoteForm) {
      props.setQuoteForm(newForm);
    } else {
      // Fallback to individual updates
      update('service_street', suggestion.street || '');
      update('service_city', suggestion.city || '');
      update('service_state', suggestion.state || '');
      update('service_zip', suggestion.zip || '');
    }
    
    // Store verified coordinates and full address
    if (setVerifiedCoords && suggestion.lat && suggestion.lng) {
      setVerifiedCoords({ 
        lat: suggestion.lat, 
        lng: suggestion.lng,
        fullAddress: suggestion.fullAddress || ''
      });
    }
  }
  
  // Clear verified coords when manual editing
  function handleManualAddressChange(field, value) {
    update(field, value);
    if (setVerifiedCoords) {
      setVerifiedCoords(null);
    }
  }
  
  // Check if server has these fee types configured
  var defaultPricing = server.default_pricing || {};
  var printingConfig = server.printing_config || {};
  
  var hasMileage = defaultPricing.mileage_rate != null;
  var hasPrinting = printingConfig.enabled === true;
  var hasExtraAttempts = defaultPricing.additional_attempt_fee != null;
  var hasAnyAdditionalFees = hasMileage || hasPrinting || hasExtraAttempts;
  
  // Calculate free pages based on number of serves
  var numServes = parseInt(form.num_serves) || 1;
  var freePagesPerServe = printingConfig.free_pages || 0;
  var totalFreePages = freePagesPerServe * numServes;
  
  // Get estimated total and breakdown
  var estimate = props.estimatedTotal;
  var total = estimate ? estimate.total : null;
  var breakdown = estimate ? estimate.breakdown : [];
  
  return (
    <form onSubmit={props.handleQuoteSubmit} className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="quote-client-name" className="text-slate-300">Your Name *</Label>
          <Input id="quote-client-name" name="client_name" required value={form.client_name} onChange={function(e) { update('client_name', e.target.value); }} className="bg-slate-800 border-slate-700 text-white" autoComplete="name" />
        </div>
        <div>
          <Label htmlFor="quote-client-company" className="text-slate-300">Company</Label>
          <Input id="quote-client-company" name="client_company" value={form.client_company} onChange={function(e) { update('client_company', e.target.value); }} className="bg-slate-800 border-slate-700 text-white" autoComplete="organization" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="quote-client-email" className="text-slate-300">Email *</Label>
          <Input id="quote-client-email" name="client_email" type="email" required value={form.client_email} onChange={function(e) { update('client_email', e.target.value); }} className="bg-slate-800 border-slate-700 text-white" autoComplete="email" />
        </div>
        <div>
          <Label htmlFor="quote-client-phone" className="text-slate-300">Phone *</Label>
          <Input id="quote-client-phone" name="client_phone" required value={form.client_phone} onChange={function(e) { update('client_phone', e.target.value); }} className="bg-slate-800 border-slate-700 text-white" autoComplete="tel" />
        </div>
      </div>
      
      {/* Address Autocomplete Search */}
      <div ref={containerRef} className="relative">
        <Label htmlFor="quote-address-search" className="text-slate-300 flex items-center gap-2">
          Service Address *
          {props.verifiedCoords && !mileageCalc.isEstimate && (
            <span className="text-xs text-emerald-400 font-normal flex items-center gap-1">
              <Navigation className="h-3 w-3" />
              Verified
            </span>
          )}
          {mileageCalc.isEstimate && (
            <span className="text-xs text-amber-400 font-normal flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              ZIP estimate
            </span>
          )}
          {!props.verifiedCoords && form.service_street && !mileageCalc.calculated && (
            <span className="text-xs text-slate-400 font-normal">
              Use autocomplete for accurate distance
            </span>
          )}
        </Label>
        <div className="relative mt-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 z-10" />
          <input
            id="quote-address-search"
            type="text"
            value={addressSearch}
            onChange={function(e) {
              setAddressSearch(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={function() { setShowSuggestions(true); }}
            placeholder="Start typing address (e.g., 2322 93rd St...)"
            className="w-full h-10 pl-10 pr-3 rounded-md bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500/50 transition-all text-sm"
            autoComplete="off"
            data-testid="quote-address-search"
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 animate-spin" />
          )}
        </div>
        
        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-xl overflow-hidden">
            <div className="py-1 max-h-48 overflow-y-auto">
              {suggestions.map(function(suggestion) {
                return (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={function() { handleAddressSelect(suggestion); }}
                    className="w-full px-3 py-2 text-left hover:bg-slate-700 transition-colors flex items-start gap-2"
                    data-testid={'address-suggestion-' + suggestion.id}
                  >
                    <Navigation className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-white block truncate">{suggestion.street}</span>
                      <span className="text-xs text-slate-400 block truncate">
                        {suggestion.city}, {suggestion.state} {suggestion.zip}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* No results message */}
        {showSuggestions && addressSearch.length >= 3 && !isLoading && suggestions.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-xl p-3">
            <p className="text-sm text-slate-400">No matching addresses found. Enter manually below.</p>
          </div>
        )}
      </div>
      
      {/* Manual address fields - always shown for editing/verification */}
      <div className="space-y-3 pt-2 border-t border-slate-700/50">
        <div>
          <Label htmlFor="quote-service-street" className="text-slate-400 text-xs">Street Address</Label>
          <Input id="quote-service-street" name="service_street" required placeholder="Street Address" value={form.service_street} onChange={function(e) { handleManualAddressChange('service_street', e.target.value); }} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label htmlFor="quote-service-city" className="text-slate-400 text-xs">City</Label>
            <Input id="quote-service-city" name="service_city" required value={form.service_city} onChange={function(e) { handleManualAddressChange('service_city', e.target.value); }} className="bg-slate-800/50 border-slate-700 text-white h-9 text-sm" />
          </div>
          <div>
            <Label htmlFor="quote-service-state" className="text-slate-400 text-xs">State</Label>
            <Input id="quote-service-state" name="service_state" required maxLength={2} placeholder="TX" value={form.service_state} onChange={function(e) { handleManualAddressChange('service_state', e.target.value.toUpperCase()); }} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm" />
          </div>
          <div>
            <Label htmlFor="quote-service-zip" className="text-slate-400 text-xs">ZIP</Label>
            <Input id="quote-service-zip" name="service_zip" required value={form.service_zip} onChange={function(e) { handleManualAddressChange('service_zip', e.target.value); }} className="bg-slate-800/50 border-slate-700 text-white h-9 text-sm" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="quote-document-type" className="text-slate-300">Document Type *</Label>
          <Input id="quote-document-type" name="document_type" required placeholder="e.g., Subpoena, Citation, Summons" value={form.document_type} onChange={function(e) { update('document_type', e.target.value); }} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
        </div>
        <div>
          <Label htmlFor="quote-urgency" className="text-slate-300">Urgency *</Label>
          <Select value={form.urgency} onValueChange={function(v) { update('urgency', v); }}>
            <SelectTrigger id="quote-urgency" className="bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="standard" className="text-slate-300 focus:bg-slate-800 focus:text-white">Standard (3-5 days)</SelectItem>
              {hasRush && (
                <SelectItem value="rush" className="text-slate-300 focus:bg-slate-800 focus:text-white">Rush ({rushHours} hours) +${rushFee}</SelectItem>
              )}
              {hasSameDay && (
                <SelectItem value="same_day" className="text-slate-300 focus:bg-slate-800 focus:text-white">Same Day +${sameDayFee}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="quote-num-serves" className="text-slate-300">Number of Serves</Label>
        <Input id="quote-num-serves" name="num_serves" type="number" min={1} value={form.num_serves} onChange={function(e) { update('num_serves', e.target.value); }} className="bg-slate-800 border-slate-700 text-white" />
      </div>
      
      {/* Conditional Additional Fee Fields */}
      {hasAnyAdditionalFees && (
        <div className="pt-3 border-t border-slate-700">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Additional Fee Inputs</p>
          <div className="grid grid-cols-1 gap-3">
            {hasMileage && (
              <div>
                <Label htmlFor="quote-estimated-miles" className="text-slate-300 flex items-center gap-2">
                  {mileageCalc.calculated ? 'Roundtrip Miles' : 'Estimated Roundtrip Miles *'}
                  <span className="text-xs text-slate-500 font-normal">(${defaultPricing.mileage_rate}/mi after {defaultPricing.free_miles || 0} free)</span>
                </Label>
                {mileageCalc.calculating ? (
                  <div className="flex items-center gap-2 h-10 px-3 bg-slate-800 border border-slate-700 rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin text-teal-400" />
                    <span className="text-sm text-slate-400">Calculating distance...</span>
                  </div>
                ) : mileageCalc.calculated ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-md">
                      <Navigation className="h-4 w-4 text-teal-400" />
                      <span className="text-white font-medium">{mileageCalc.totalMiles} mi</span>
                      <span className="text-xs text-slate-400">roundtrip</span>
                      {mileageCalc.isEstimate && (
                        <span className="text-xs text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded">ZIP estimate</span>
                      )}
                      {mileageCalc.billableMiles > 0 && (
                        <span className="ml-auto text-xs text-amber-400">({mileageCalc.billableMiles} mi billable)</span>
                      )}
                    </div>
                    {mileageCalc.closestOffice && (
                      <p className="text-xs text-slate-500">From: {mileageCalc.closestOffice}</p>
                    )}
                    <input type="hidden" name="estimated_miles" value={form.estimated_miles} />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Input 
                      id="quote-estimated-miles" 
                      name="estimated_miles" 
                      type="number" 
                      min={0}
                      step="0.1"
                      required
                      placeholder="Enter roundtrip miles"
                      value={form.estimated_miles} 
                      onChange={function(e) { update('estimated_miles', e.target.value); }} 
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="quote-estimated-miles"
                    />
                    {mileageCalc.error && (
                      <p className="text-xs text-amber-400">{mileageCalc.error} - Enter miles manually</p>
                    )}
                    <p className="text-xs text-slate-500">Enter complete address above to auto-calculate</p>
                  </div>
                )}
              </div>
            )}
            {hasPrinting && (
              <div>
                <Label htmlFor="quote-pages-to-print" className="text-slate-300 flex items-center gap-2">
                  Pages per Service Packet *
                  <span className="text-xs text-slate-500 font-normal">
                    ({freePagesPerServe} free/serve, ${printingConfig.per_page_fee}/page after)
                  </span>
                </Label>
                <Input 
                  id="quote-pages-to-print" 
                  name="pages_to_print" 
                  type="number" 
                  min={0}
                  required
                  placeholder="e.g., 10"
                  value={form.pages_to_print} 
                  onChange={function(e) { update('pages_to_print', e.target.value); }} 
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="quote-pages-to-print"
                />
                {numServes > 1 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {form.pages_to_print ? (parseInt(form.pages_to_print) * numServes) + ' total pages for ' + numServes + ' serves' : 'Total pages = pages/packet × serves'}
                  </p>
                )}
              </div>
            )}
            {hasExtraAttempts && (
              <div>
                <Label htmlFor="quote-extra-attempts" className="text-slate-300 flex items-center gap-2">
                  Extra Attempts Anticipated *
                  <span className="text-xs text-slate-500 font-normal">(${defaultPricing.additional_attempt_fee} each)</span>
                </Label>
                <Input 
                  id="quote-extra-attempts" 
                  name="extra_attempts" 
                  type="number" 
                  min={0}
                  required
                  placeholder="e.g., 1"
                  value={form.extra_attempts} 
                  onChange={function(e) { update('extra_attempts', e.target.value); }} 
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="quote-extra-attempts"
                />
              </div>
            )}
          </div>
        </div>
      )}
      
      <div>
        <Label htmlFor="quote-additional-details" className="text-slate-300">Additional Details</Label>
        <Textarea id="quote-additional-details" name="additional_details" placeholder="Any special instructions..." value={form.additional_details} onChange={function(e) { update('additional_details', e.target.value); }} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
      </div>
      
      {/* Itemized Cost Breakdown */}
      <div className="p-4 bg-slate-800/60 rounded-lg border border-slate-700" data-testid="quote-cost-breakdown">
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Estimated Cost Breakdown</p>
        {breakdown.length > 0 ? (
          <div className="space-y-2 mb-3">
            {breakdown.map(function(item, idx) {
              return (
                <div key={idx} className="flex flex-col">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="font-medium text-white font-mono">${item.amount.toFixed(2)}</span>
                  </div>
                  {item.detail && (
                    <span className="text-xs text-slate-500 mt-0.5">{item.detail}</span>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
        <div className="flex justify-between items-center pt-2 border-t border-slate-700">
          <span className="text-slate-300 font-medium">Estimated Total</span>
          <span className="text-xl font-bold text-teal-400 font-mono">
            {total ? '$' + total.toFixed(2) : '—'}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-2">Final quote may vary based on actual service conditions.</p>
      </div>
      
      {/* Coverage Warning/Block */}
      {coverageCheck.checked && coverageCheck.isInCoverage === false && (
        <div 
          className={`p-4 rounded-lg border ${coverageCheck.blockSubmission ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}
          data-testid="coverage-warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${coverageCheck.blockSubmission ? 'text-red-400' : 'text-amber-400'}`} />
            <div className="flex-1">
              <p className={`font-medium ${coverageCheck.blockSubmission ? 'text-red-400' : 'text-amber-400'}`}>
                {coverageCheck.blockSubmission ? 'Service Area Not Covered' : 'Possible Out-of-Area Address'}
              </p>
              <p className={`text-sm mt-1 ${coverageCheck.blockSubmission ? 'text-red-400/80' : 'text-amber-400/80'}`}>
                {coverageCheck.blockSubmission 
                  ? `${serverName} only accepts quotes from their listed coverage areas. Please contact them directly if you believe they can still serve this location.`
                  : `This address may be outside ${serverName}'s listed coverage areas. You can still submit the request, but they may not be able to serve this location.`
                }
              </p>
              {server.phone && coverageCheck.blockSubmission && (
                <a 
                  href={'tel:' + server.phone} 
                  className="inline-flex items-center gap-1.5 mt-2 text-sm text-teal-400 hover:text-teal-300"
                >
                  <Phone className="h-4 w-4" />
                  Call {server.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      
      {coverageCheck.checking && (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking coverage...
        </div>
      )}
      
      <Button 
        type="submit" 
        className="w-full btn-accent" 
        disabled={props.submitting || coverageCheck.blockSubmission} 
        data-testid="submit-quote-btn"
      >
        {props.submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        {props.submitting ? 'Sending...' : coverageCheck.blockSubmission ? 'Submission Blocked' : 'Submit Quote Request'}
      </Button>
    </form>
  );
}

export default ServerProfilePage;
